import { Helmet } from "react-helmet-async";
import Button from "./components/Button.js";
import PrintCarousel from "./components/PrintCarousel/PrintCarousel.js";
import ShopBy from "./components/ShopBy/ShopBy.js";
import { HOMEPAGE_STRUCTURED_DATA } from "./utility/homepageStructuredData.js";

const STEPS = [
  {
    image: "/homepage/step-1.png",
    title: "Choose the type and style",
    body: "Choose from portrait, landscape or square and optionally select the art style of the print you want to create.",
  },
  {
    image: "/homepage/step-2.png",
    title: "Describe or upload",
    body: "Describe your desired print in words or use a photo to match an existing print or style of your interior.",
  },
  {
    image: "/homepage/step-3.png",
    title: "Order your unique print",
    body: "Now your print has been created, order the print with a range of options including size, frame and mount.",
  },
];

const Home = () => {
  return (
    <main>
      <Helmet>
        <title>
          Create Prints - AI Art Prints Matched to Your Room
        </title>
        <meta
          name="description"
          content="Generate one-of-a-kind AI art prints matched to your room by colour and light, or browse a catalogue analysed for room fit. Printed to order in the UK and US, delivered without import fees."
        />
        <link
          rel="preload"
          as="image"
          type="image/avif"
          href="/create-prints-hero-768.avif"
          imagesrcset="/create-prints-hero-480.avif 480w, /create-prints-hero-768.avif 768w"
          imagesizes="(min-width: 1024px) 50vw, calc(100vw - 3rem)"
          fetchpriority="high"
        />
        <script type="application/ld+json">
          {JSON.stringify(HOMEPAGE_STRUCTURED_DATA)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="mx-auto grid max-w-[100rem] items-center gap-10 px-6 pb-12 pt-8 lg:grid-cols-2 lg:gap-16 lg:py-16 lg:pr-0">
        <div className="lg:pl-8 xl:pl-16">
          <h1 className="font-serif text-5xl font-bold leading-[1.05] text-slate-950 sm:text-6xl xl:text-7xl">
            Create Unique
            <br /> Art Prints
          </h1>
          <p className="mt-6 max-w-lg text-lg text-slate-600 sm:text-xl">
            Need art for your walls? Turn your own words or photos into unique
            art prints, that magically match the style of your interior.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button url="/generate-print">Create Your Art Print</Button>
            <Button url="/prints" variant="secondary">
              Shop all prints
            </Button>
          </div>
        </div>

        <div className="lg:h-[36rem]">
          <picture>
            <source
              type="image/avif"
              srcSet="/create-prints-hero-480.avif 480w, /create-prints-hero-768.avif 768w"
              sizes="(min-width: 1024px) 50vw, calc(100vw - 3rem)"
            />
            <source
              type="image/webp"
              srcSet="/create-prints-hero-480.webp 480w, /create-prints-hero-768.webp 768w"
              sizes="(min-width: 1024px) 50vw, calc(100vw - 3rem)"
            />
            <img
              src="/create-prints-hero-768.webp"
              alt="Art prints hanging on an interior wall"
              width="768"
              height="768"
              fetchpriority="high"
              decoding="async"
              className="h-full w-full rounded-2xl object-cover lg:rounded-l-2xl lg:rounded-r-none"
            />
          </picture>
        </div>
      </section>

      {/* Featured prints */}
      <PrintCarousel
        title="Featured Prints"
        subtitle="Browse prints generated by other users"
        viewAllUrl="/prints"
        featured
        limit={12}
      />

      {/* Shop by room / mood */}
      <ShopBy />

      {/* How it works */}
      <section className="bg-[#E5E5DC] py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
              How does it work?
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Create unique art prints for your home in three simple steps.
            </p>
          </div>

          <div className="mt-12 flex flex-col gap-12 md:flex-row md:items-center md:gap-16">
            <div className="flex justify-center md:w-1/2">
              <div className="relative flex w-72 items-center justify-center rounded-3xl border-8 border-black bg-black p-1">
                <video
                  controls
                  playsInline
                  preload="none"
                  poster={process.env.PUBLIC_URL + "/how-it-works-poster.webp"}
                >
                  <source
                    src={
                      process.env.PUBLIC_URL +
                      "/create-prints-ai-how-it-works.mp4"
                    }
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <ol className="md:w-1/2">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className={`flex items-start gap-6 ${
                    index < STEPS.length - 1 ? "mb-10" : ""
                  }`}
                >
                  <img
                    src={step.image}
                    width="80"
                    height="80"
                    loading="lazy"
                    decoding="async"
                    className="h-20 w-20 flex-none"
                    alt={`Step ${index + 1}`}
                  />
                  <div>
                    <h3 className="font-serif text-xl font-bold text-slate-950">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-base text-slate-600">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16 sm:py-24">
        <div className="rounded-3xl bg-slate-950 px-6 py-14 text-center sm:px-12">
          <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
            Can't find the one? Make it.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
            Describe the piece you have in mind, or upload a photo of your room,
            and we'll generate a print made to match it.
          </p>
          <div className="mt-8 flex justify-center">
            <Button url="/generate-print" variant="inverse">
              Create Your Art Print
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
